import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { computeSalesOrderTotals } from '../../sales-order-totals/compute-sales-order-totals.js';
/** The Review step's figures from the staged form, through the same engine the server stores totals with. */
export function resolveAdjustmentTotals({ lines, adjustments, totalIncludesTax = false, }) {
    const shipping = adjustments.find((adjustment) => adjustment.kind === 'shipping');
    const discount = adjustments.find((adjustment) => adjustment.kind === 'discount');
    const taxes = adjustments.filter((adjustment) => adjustment.kind === 'tax');
    const discountAmount = discount?.amount ?? 0;
    const discountType = discount?.discountType ?? 'flat';
    const totals = computeSalesOrderTotals({
        productLines: lines.map((line, index) => ({ key: String(index), ...line })),
        shipping: shipping?.amount ?? null,
        discount: discountType === 'percentage'
            ? { type: 'percentage', percent: discountAmount }
            : { type: 'flat', amount: discountAmount },
        taxes: taxes.map((tax, index) => ({ key: String(index), percent: tax.percent })),
        totalIncludesTax,
    });
    return {
        productSubtotal: totals.productSubtotal.toNumber(),
        shippingFee: totals.shipping.toNumber(),
        taxes: taxes.map((tax, index) => ({
            taxRateId: tax.taxRateId,
            name: tax.name,
            percent: tax.percent,
            amount: totals.taxAmounts.get(String(index))?.toNumber() ?? 0,
        })),
        taxAmount: totals.totalTax.toNumber(),
        discountAmount,
        discountType,
        discountTotal: totals.discountApplied.toNumber(),
        total: totals.total.toNumber(),
    };
}
// What the rep entered, not what it comes to: the server turns it into amounts (shipping as
// entered, discount capped at the product subtotal, tax on the discounted base), so the stored
// order always matches the totals engine rather than a client-side guess.
function buildAdjustmentInputs(values) {
    return values.adjustments.flatMap((adjustment) => {
        if (adjustment.kind === 'shipping') {
            return adjustment.amount
                ? [{ typeId: SalesOrderItemType.SHIPPING, amount: adjustment.amount }]
                : [];
        }
        if (adjustment.kind === 'tax') {
            return adjustment.percent
                ? [
                    {
                        typeId: SalesOrderItemType.TAX,
                        percent: adjustment.percent,
                        taxRateId: adjustment.taxRateId,
                    },
                ]
                : [];
        }
        return adjustment.amount
            ? [
                {
                    typeId: SalesOrderItemType.DISCOUNT,
                    amount: adjustment.amount,
                    discountType: adjustment.discountType,
                },
            ]
            : [];
    });
}
// Replaces the old createSalesOrder + N * addSalesOrderItem + setSalesOrderSamplePurpose
// sequence with a single atomic mutation input -- see create-draft-sales-order.ts (apps/api).
export function toBookSalesOrderInput(values) {
    return {
        customerId: values.customerId,
        brandId: values.brandId,
        locationGroupId: values.locationGroupId,
        paymentTermsId: values.paymentTermsId,
        note: values.note.trim() || undefined,
        // null (not undefined) for empty -- this wizard always has a definite, full-fidelity value
        // for these fields, so "empty" means "actively cleared," not "not addressed." See
        // BookSalesOrderMutationInput's customFields-merge contract above.
        internalNotes: values.internalNotes.trim() || null,
        deliveryPreferences: values.deliveryPreferences.trim() || null,
        customerContactId: values.customerContactId.trim() || null,
        samplePurpose: values.samplePurpose ?? null,
        lines: values.lines.map((line) => ({
            productId: line.productId,
            qty: line.qty,
            unitPrice: line.unitPrice,
            typeId: line.typeId,
            note: line.note.trim() || undefined,
            uomId: line.uomId || undefined,
        })),
        adjustments: buildAdjustmentInputs(values),
        applyTaxDefaults: false,
    };
}
//# sourceMappingURL=book-mapper.js.map